import type {
  InterventionSectionAsset,
  InterventionSectionItem,
  InterventionSectionObservation,
} from "./intervention-section.type";
import type {
  InterventionTemplateDetail,
  InterventionTemplateNode,
} from "@/entities/intervention-template";

export const isLoopOnChildrenSection = (
  section: InterventionSectionItem
): boolean => {
  return (
    ("is_loop_on_children" in section && section.is_loop_on_children) ?? false
  );
};

export const isDynamicSection = (section: InterventionSectionItem): boolean => {
  return ("is_dynamic" in section && section.is_dynamic) ?? false;
};

export const getInterventionSectionAsset = (
  section?: InterventionSectionItem
): InterventionSectionAsset | undefined => {
  if (!section) return undefined;
  if ("asset" in section) return section.asset;
  return undefined;
};

export const getInterventionSectionObservation = (
  section?: InterventionSectionItem
): InterventionSectionObservation | undefined => {
  if (!section) return undefined;
  if ("progress" in section) return section;
  if ("observation" in section) return section.observation;
  return undefined;
};

export const getInterventionSectionParams = (
  section?: InterventionSectionItem
) => {
  return {
    layoutIndex: getInterventionSectionLayoutIndex(section),
    assetId: getInterventionSectionAssetId(section),
    observationId: getInterventionSectionObservationId(section),
  };
};

export const getInterventionSectionLayoutIndex = (
  section?: InterventionSectionItem
): number | undefined => {
  return section?.layout_index;
};

export const getInterventionSectionAssetId = (
  section?: InterventionSectionItem
): number | undefined => {
  if (!section) return undefined;
  if ("asset_id" in section) return section.asset_id;
  return getInterventionSectionAsset(section)?.id;
};

export const getInterventionSectionObservationId = (
  section?: InterventionSectionItem
): number | undefined => {
  return getInterventionSectionObservation(section)?.id;
};

export const getInterventionSectionSections = (
  section?: InterventionSectionItem
) => {
  if (!section) return [];
  if ("sections" in section) return section.sections ?? [];
  return [];
};

export const getInterventionSectionObservations = (
  section?: InterventionSectionItem
) => {
  if (!section) return [];
  if ("observations" in section) return section.observations ?? [];
  return [];
};

export const getInterventionSectionTemplate = (
  section?: InterventionSectionItem,
  template?: InterventionTemplateDetail
) => {
  if (!section?.layout_index || !template) return undefined;

  if (template.layout_index === section.layout_index) {
    return template;
  }

  return findInterventionSectionTemplateNode(section, template.templates);
};

const findInterventionSectionTemplateNode = (
  section?: InterventionSectionItem,
  templates?: InterventionTemplateNode[]
): InterventionTemplateNode | undefined => {
  if (!section?.layout_index || !templates?.length) return undefined;

  let nodes: InterventionTemplateNode[] = [];

  for (const template of templates) {
    if (template.layout_index === section.layout_index) {
      return template;
    }

    const templateNodes = "templates" in template ? template.templates : [];
    nodes = [...nodes, ...(templateNodes ?? [])];
  }

  return findInterventionSectionTemplateNode(section, nodes);
};
